import React, {
  DragEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Search, X } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDnD } from '@/context/dnd-context';
import { cn } from '@/lib/utils';
import {
  isPickerHiddenNode,
  matchesNodeSearch,
  sortLogicNodes,
} from '@/constants/node-catalog';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { NodeStruct } from '@/types/node-types';

import { AddNodesList } from './add-nodes-list';
// --- Phase 6: insertable examples for the logic nodes ---
import { LogicExamplesList } from './logic-examples-list';
// --- end Phase 6 ---
import { NoResultsMessage } from './no-results-message';
import { RightPanelSlot, rightPanelCardClasses } from './right-panel-slot';

interface AddNodesPanelProps {
  isLocked?: boolean;
}

const DEFAULT_OPEN_CATEGORIES = 3;

export const AddNodesPanel: React.FC<AddNodesPanelProps> = ({
  isLocked = false,
}) => {
  const isAddNodesOpen = useWorkflowPanelStore((state) => state.isAddNodesOpen);
  const closeAddNodesPanel = useWorkflowPanelStore(
    (state) => state.closeAddNodesPanel,
  );
  const panelSize = useWorkflowPanelStore((state) => state.panelSize);
  const groupedNodes = useNodeStore((state) => state.groupedNodes);
  const isLoadingNodeList = useNodeStore((state) => state.isLoadingNodeList);
  const addNode = useNodeStore((state) => state.addNode);
  const openWorkflowPanel = useWorkflowPanelStore(
    (state) => state.openWorkflowPanel,
  );
  const setConsolePanelOpen = useWorkflowPanelStore(
    (state) => state.setConsolePanelOpen,
  );
  const [, setDndPayload] = useDnD();
  const { setCenter, getZoom } = useReactFlow();

  const [search, setSearch] = useState('');
  const [openCategories, setOpenCategories] = useState<string[] | null>(null);
  const defaultOpenCategoriesRef = useRef<string[]>([]);

  const filteredNodes = useMemo(() => {
    const base: Record<string, NodeStruct[]> = {};
    Object.entries(groupedNodes).forEach(([category, nodes]) => {
      const visible = nodes.filter((node) => !isPickerHiddenNode(node));
      // --- if/else (Phase 8b): If / Else before Switch in Logic ---
      if (visible.length > 0) {
        base[category] =
          category === 'logic' ? sortLogicNodes(visible) : visible;
      }
    });

    const query = search.trim().toLowerCase();
    if (!query) return base;

    const matches: Record<string, NodeStruct[]> = {};
    Object.entries(base).forEach(([category, nodes]) => {
      // Display name or a plain-word alias ("if", "condition", "branch").
      const matching = nodes.filter((node) => matchesNodeSearch(node, query));
      if (matching.length > 0) matches[category] = matching;
    });
    return matches;
  }, [groupedNodes, search]);

  const sortedCategories = useMemo(
    () =>
      Object.keys(filteredNodes).sort(
        (a, b) =>
          (filteredNodes[a]?.length || 0) - (filteredNodes[b]?.length || 0),
      ),
    [filteredNodes],
  );

  // Several groups stand open at once, unlike the left panel's accordion. Until
  // the user touches one, the first few are expanded.
  const effectiveOpenCategories =
    openCategories ?? sortedCategories.slice(0, DEFAULT_OPEN_CATEGORIES);
  defaultOpenCategoriesRef.current = effectiveOpenCategories;

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>, node: NodeStruct) => {
      if (isLocked) {
        event.preventDefault();
        return;
      }
      setDndPayload({ type: 'node', nodeId: node.node_id });
      event.dataTransfer.effectAllowed = 'move';
    },
    [isLocked, setDndPayload],
  );

  const handleToggleCategory = useCallback((category: string) => {
    setOpenCategories((previous) => {
      const current = previous ?? defaultOpenCategoriesRef.current;
      return current.includes(category)
        ? current.filter((name) => name !== category)
        : [...current, category];
    });
  }, []);

  const handleAddNode = useCallback(
    (node: NodeStruct) => {
      if (isLocked) return;
      addNode(node, undefined, {
        setCenter,
        zoom: getZoom(),
        rightSidebarWidthPx: panelSize,
      });
      // A fresh node has nothing under Events, so the inspector opens where
      // the work is. Opening it also hands the slot back from this panel.
      openWorkflowPanel('configure');
      setConsolePanelOpen(true);
    },
    [
      addNode,
      getZoom,
      isLocked,
      openWorkflowPanel,
      panelSize,
      setCenter,
      setConsolePanelOpen,
    ],
  );

  if (!isAddNodesOpen) return null;

  const hasResults = sortedCategories.length > 0;

  return (
    <RightPanelSlot width={panelSize}>
      <div className={cn(rightPanelCardClasses, 'h-full')}>
        <div className='flex shrink-0 items-center justify-between gap-2 p-4'>
          <span className='text-base font-medium'>Add Nodes</span>
          <Button
            variant='ghost'
            size='icon'
            className='size-9 shrink-0'
            onClick={closeAddNodesPanel}
            aria-label='Close Add Nodes'
          >
            <X className='size-4' />
          </Button>
        </div>

        {isLoadingNodeList ? (
          <div className='flex flex-col gap-4 p-4'>
            <Skeleton className='h-9 w-full rounded-md' />
            {Array.from({ length: 3 }).map((_, groupIdx) => (
              <div key={groupIdx} className='flex flex-col gap-1.5'>
                <Skeleton className='mb-1 h-4 w-24' />
                {Array.from({ length: 3 }).map((_, itemIdx) => (
                  <Skeleton
                    key={itemIdx}
                    className={`h-8 ${itemIdx === 2 ? 'w-3/4' : 'w-full'}`}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className='shrink-0 px-4 pb-4'>
              <div className='relative'>
                <Search className='absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder='Search Nodes'
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className='pr-8 pl-10'
                />
                {search && (
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => setSearch('')}
                    className='absolute top-1/2 right-1 size-6 -translate-y-1/2'
                    aria-label='Clear search'
                  >
                    <X className='size-3' />
                  </Button>
                )}
              </div>
            </div>
            <div className='min-h-0 flex-1 overflow-y-auto pb-4'>
              {/* --- Phase 6: examples first, so a blank canvas has somewhere to start --- */}
              <LogicExamplesList
                isLocked={isLocked}
                search={search}
                compact={!search}
                onInserted={closeAddNodesPanel}
              />
              {/* --- end Phase 6 --- */}
              {hasResults ? (
                <AddNodesList
                  nodesByCategory={filteredNodes}
                  categories={sortedCategories}
                  openCategories={effectiveOpenCategories}
                  onToggleCategory={handleToggleCategory}
                  onDragStart={handleDragStart}
                  onAddNode={handleAddNode}
                  isLocked={isLocked}
                  search={search}
                />
              ) : (
                <NoResultsMessage
                  onClearSearch={() => setSearch('')}
                  hasSearchQuery={search.length > 0}
                />
              )}
            </div>
          </>
        )}
      </div>
    </RightPanelSlot>
  );
};
